<template>
  <CompanySkeletonLoader v-if="isLoading" />
  <v-alert v-else-if="!company" type="warning" icon="mdi-alert" class="mb-0">
    {{ $t('entities.arenas.noCompany', { arenaName }) }}
  </v-alert>
  <v-card v-else outlined>
    <v-card-title class="justify-space-between align-center flex-wrap">
      <div>
        <div class="text-h6 font-weight-bold">{{ company.name }}</div>
        <div class="grey--text text--darken-1">#{{ company.id }}</div>
      </div>
      <v-btn
        v-if="canEditCompany"
        color="primary"
        small
        class="mt-2 mt-md-0"
        @click="openUpsertDialog">
        {{ $t('miscellaneous.edit') }}
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 grey--text">
            {{ $t('entities.companies.attributes.legalName') }}
          </div>
          <div class="text-body-1 font-weight-medium">
            {{ company.legal_name || '—' }}
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 grey--text">
            {{ $t('entities.companies.attributes.taxNumber') }}
          </div>
          <div class="text-body-1 font-weight-medium">
            {{ company.tax_number || '—' }}
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 grey--text">
            {{ $t('generalAttributes.address') }}
          </div>
          <div class="text-body-1 font-weight-medium">
            {{ company.address1 }}<br />
            <span v-if="company.address2">{{ company.address2 }}<br /></span>
            {{ cityAndPostalCode }}<br />
            {{ company.country }}
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="text-subtitle-2 grey--text">
            {{ $t('generalAttributes.contact') }}
          </div>
          <div class="text-body-1 font-weight-medium">
            <div>
              {{ $t('generalAttributes.email') }}:
              {{ company.email || '—' }}
            </div>
            <div>
              {{ $t('generalAttributes.phone') }}:
              {{ company.phone || '—' }}
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
    <CompanyUpsertDialog
      v-if="canEditCompany"
      @company:upsert="upsertCompany"
      @dialog:close="closeUpsertDialog"
      :company="company || {}"
      :is-loading="isDialogSubmitting"
      :is-opened="isUpsertDialogOpen" />
  </v-card>
</template>

<script>
import Company from '@/models/setup/CompanyV2'
import ArenaCompany from '@/models/setup/ArenaCompany'
import CompanyUpsertDialog from '@/views/setup/companies/UpsertDialog.vue'
import CompanySkeletonLoader from './CompanySkeletonLoader.vue'
import isRole from '@/mixins/crud/computed/isRole'

export default {
  name: 'Company',
  components: {
    CompanyUpsertDialog,
    CompanySkeletonLoader,
  },
  mixins: [isRole],
  data() {
    return {
      company: null,
      isDialogSubmitting: false,
      isLoading: true,
      isUpsertDialogOpen: false,
    }
  },
  computed: {
    arenaName() {
      return this.$store.getters.arena?.name
    },
    arenaId() {
      return this.$store.getters.arena ? this.$store.getters.arena.id : null
    },
    cityAndPostalCode() {
      if (!this.company) return ''
      const postcode = this.company.postcode || ''
      const city = this.company.city || ''
      return `${postcode} ${city}`.trim()
    },
    canEditCompany() {
      return this.isAdmin || this.isManager
    },
  },
  methods: {
    closeUpsertDialog() {
      this.isUpsertDialogOpen = false
    },
    async getCompany() {
      this.isLoading = true
      try {
        const response = await ArenaCompany.api.getCompany(this.arenaId)
        this.company = new Company(response?.data || {})
      } catch (error) {
        if (error?.response?.status === 404) {
          this.company = null
        } else {
          console.error(error)
        }
      } finally {
        this.isLoading = false
      }
    },
    openUpsertDialog() {
      this.isUpsertDialogOpen = true
    },
    sanitizePayload(payload) {
      const { id, ...payloadWithoutId } = payload
      return id ? { id, ...payloadWithoutId } : payloadWithoutId
    },

    async updateCompany(companyId, body) {
      return this.isManager
        ? await ArenaCompany.api.updateCompany(this.arenaId, companyId, body)
        : await Company.api.update(companyId, body)
    },
    async upsertCompany(payload) {
      if (!this.arenaId) return
      this.isDialogSubmitting = true
      const body = this.sanitizePayload(payload)
      const companyId = payload?.id
      try {
        if (companyId) {
          await this.updateCompany(companyId, body)
        } else {
          const response = await Company.api.create(body)
          const createdCompany = response?.data?.data || response?.data
          const newCompanyId = createdCompany?.id
          if (newCompanyId) {
            await Company.api.addArena(newCompanyId, { arena_id: this.arenaId })
          }
        }
        await this.getCompany()
        this.closeUpsertDialog()
      } catch (error) {
        console.error(error)
      } finally {
        this.isDialogSubmitting = false
      }
    },
  },
  async mounted() {
    await this.getCompany()
  },
}
</script>
